<template>
  <div class="!m-0 divider pt-3"><hr class="!m-0" /></div>
  <div class="scroll-container pt-2">
    <ExplainerVideo
      v-if="shouldShowExplainerVideo"
      :src="explainerVideoSrc.video"
      :poster="explainerVideoSrc.thumbnail"
      :overlay-text="overlayText"
    />
    <div class="tile-grid-photos gap-1 mt-2">
      <InspirationTiles
        :selected-image-state="props.selectedImageState"
        :templates="currentForegroundInspirations"
        :is-loading="isLoading"
        :image-button-size="buttonSize"
      />
    </div>
    <div class="pb-3"></div>
    <div class="absolute bottom-4 z-20 generate-widget-mobile md:hidden">
      <GenerateWidget
        v-if="shouldShowGenerateWidget"
        :template="props.selectedImageState.selectedMagicBackgroundTemplate"
        :persistent-store="props.selectedImageState"
      />
      <SelectInspirationGuideWidget
        v-if="shouldShowSelectInspirationWidget && isMagicBackgroundsEnabled"
        :hideWidget="hideSelectInspirationWidget"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import Client from "@/modules/internal_api/client";
import { MagicBackgroundForegroundType, MagicBackgroundTemplate } from "@/modules/internal_api/magic_backgrounds";
import { withExponentialBackoff } from "@/modules/utils";
import { useEditorStore } from "@/stores/editor_store";
import { PersistentStore } from "@/stores/persistent_store";
import { computed, inject, onMounted, ref } from "vue";
import InspirationTiles from "@/components/prism/inspiration_tiles.vue";
import { useMonitorSize } from "@/composables/monitor_size";
import ExplainerVideo from "@/components/upload/ai_generate/explainer_video.vue";

import animalVideoMobile from "@assets/images/magic_backgrounds/mobile/animal.mp4";
import carVideoMobile from "@assets/images/magic_backgrounds/mobile/car.mp4";
import personVideoMobile from "@assets/images/magic_backgrounds/mobile/person.mp4";
import productVideoMobile from "@assets/images/magic_backgrounds/mobile/product.mp4";

import animalThumbnailMobile from "@assets/images/magic_backgrounds/mobile/animal_thumbnail.png";
import carThumbnailMobile from "@assets/images/magic_backgrounds/mobile/car_thumbnail.png";
import personThumbnailMobile from "@assets/images/magic_backgrounds/mobile/person_thumbnail.png";
import productThumbnailMobile from "@assets/images/magic_backgrounds/mobile/product_thumbnail.png";

import GenerateWidget from "./generate_widget.vue";
import { useMagicBackground } from "@/composables/magic_background";
import { usePhotoEditor } from "@/composables/photo_editor";
import SelectInspirationGuideWidget from "./select_inspiration_guide_widget.vue";
import { I18n as I18nType } from "i18n-js";
import { useMagicBackgroundFlag } from "@/composables/magic_background_flag";

export interface InspirationGalleryProps {
  selectedImageState: PersistentStore;
  foregroundType: string;
  scrollContainerHeight: number;
}

const props = defineProps<InspirationGalleryProps>();
const store = useEditorStore();
const I18n: I18nType = inject("I18n");

const isLoading = ref(false);
const inspirations = computed(() => store.magicBackgrounds.inspirations);

const { isSmallTablet } = useMonitorSize();
const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

const buttonSize = "flex";
const persistentStore = computed(() => props.selectedImageState);
const { isGenerateMode, hideSelectInspirationWidget, shouldShowSelectInspirationWidget } =
  useMagicBackground(persistentStore);
const { isMagicBackgroundsEnabled } = useMagicBackgroundFlag();
const shouldShowGenerateWidget = computed(() => isMagicBackgroundsEnabled.value && isGenerateMode.value);

const overlayText = computed(() => {
  return I18n.t("editor.magic_background.variants.variant_a.guide_video_copy", {
    cta_label: I18n.t("editor.magic_background.variants.variant_a.generate"),
  });
});

onMounted(async () => {
  const allInspirations = await loadInspirations();
  store.loadInspirations(allInspirations);
});

const loadInspirations = async () => {
  try {
    isLoading.value = true;
    const templatesByType = await withExponentialBackoff(
      () => Client.fetchMagicBackgroundsTemplates({ foregroundType: props.foregroundType }),
      {
        on: [Error],
        retryLimit: 1,
      }
    );
    return templatesByType;
  } catch (err) {
    console.log(err);
  } finally {
    isLoading.value = false;
  }
};

const inspirationPriorityMap = {
  product: ["product", "person", "animal", "car"],
  person: ["person", "animal", "product", "car"],
  car: ["car", "animal", "person", "product"],
  animal: ["animal", "person", "product", "car"],
  graphics: ["animal", "product", "person", "car"],
};

const currentForegroundInspirations = computed(() => {
  if (!inspirations.value) return [];

  const currentType = (props.foregroundType ?? "other").toLowerCase() as keyof typeof inspirationPriorityMap;

  const priorityOrder = inspirationPriorityMap[currentType] || ["product", "person", "animal", "car"];

  let orderedInspirations: MagicBackgroundTemplate[] = [];

  priorityOrder.forEach((type) => {
    const typeInspirations = inspirations.value?.[type as MagicBackgroundForegroundType] || [];
    orderedInspirations = [...orderedInspirations, ...typeInspirations];
  });

  return orderedInspirations;
});

const explainerVideoSrcMap = {
  animal: {
    video: animalVideoMobile,
    thumbnail: animalThumbnailMobile,
  },
  car: {
    video: carVideoMobile,
    thumbnail: carThumbnailMobile,
  },
  person: {
    video: personVideoMobile,
    thumbnail: personThumbnailMobile,
  },
  product: {
    video: productVideoMobile,
    thumbnail: productThumbnailMobile,
  },
};

const explainerVideoSrc = computed(() => {
  const foregroundType = (props.foregroundType ?? "other").toLowerCase();

  return {
    video: explainerVideoSrcMap[foregroundType]?.video,
    thumbnail: explainerVideoSrcMap[foregroundType]?.thumbnail,
  };
});

const shouldShowExplainerVideo = computed(() => {
  return explainerVideoSrc.value.video && explainerVideoSrc.value.thumbnail;
});

const scrollContainerHeight = computed(() => {
  const height = props.scrollContainerHeight;
  const paddingSpace = 25; // Padding value that's calculated to match other panels

  if (isNewPhotoEditor.value) {
    return `${height - paddingSpace}px`;
  }

  return isSmallTablet.value ? "20rem" : "23rem";
});
</script>

<style lang="scss" scoped>
.tile-grid-photos {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  padding: 0 0.25rem;

  @media (min-width: 376px) and (max-width: 1023px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.divider {
  margin: 0 -16px !important;

  @media (min-width: 640px) {
    margin: 0 -24px !important;
  }
}

.scroll-container {
  overflow-x: hidden;
  overflow-y: auto;
  height: v-bind(scrollContainerHeight);
}

/* this feature is only supported on iOS, it's used here because scroll behavior is different on iOS and negative margin shifts the content */
// @supports not (-webkit-touch-callout: none) {
//   .scroll-container {
//     margin-right: -1rem;
//   }
// }

.scroll-container::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 1rem;
}

.scroll-container::-webkit-scrollbar-thumb {
  border: 4px solid rgba(0, 0, 0, 0);
  background-clip: padding-box;
  -webkit-border-radius: 7px;
  background-color: rgba(0, 0, 0, 0.15);
  -webkit-box-shadow: inset -1px -1px 0px rgba(0, 0, 0, 0.05), inset 1px 1px 0px rgba(0, 0, 0, 0.05);
}

.generate-widget-mobile {
  left: 50%;
  transform: translateX(-50%);
}
</style>
